/* eslint-disable import/prefer-default-export */
import * as React from "react";
import {
  Grid,
  StyleRules,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  PageTitles,
  PentairTheme,
} from "@pentair/react";
import { RouteComponentProps } from "react-router";
import {
  Application,
  makePresenter,
} from "../../../application/application";
import { DefaultAdminPagePresenter } from "./default-admin-page-presenter";
import { IMicroAppFeature } from "../../common/types";
import { ConfirmDialog } from "../../../components/confirmation-dialog";
import { useTranslate } from "../../../components/translator/use-translator";
import { MicroAppListItem } from "../partials/microapp-list-item";
import { NavigationMenuSection } from "../partials/navigation-menu-section";
import LoadingIcon from "../../../components/loading-icon";
import { IS_MOBILE_LOGIN, MICRO_APP_LOGIC_LAYER } from "../../../config/constants";
import { useNavigationConfigs } from "../../../hooks/use-navigation-configs";

/**
 * Component UX styles
 */
const useStyles = makeStyles((theme: PentairTheme): StyleRules => ({
  root: {},
  container: {
    marginTop: 55,
    width: "95%",
    transition: "width 0.3s",
    background: "#fff",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      height: "100vh",
      marginTop: 0,
      marginRight: 0,
      marginLeft: 0,
    },
    [theme.breakpoints.between("xs", "sm")]: {
      height: "unset",
      marginTop: 0,
      marginRight: 0,
      marginLeft: 0,
    },
  },
}));

/**
 * An utility to create the presenter instance
 */
const usePresenter = makePresenter(
  ({ network }: Application) => new DefaultAdminPagePresenter(network),
  (presenter, props: RouteComponentProps) => {
    presenter.history = props.history;
    presenter.location = props.location;
  },
);

/**
 * A component to manage the micro-apps as administration page
 */
export const DefaultAdminPage: React.FC<RouteComponentProps> = (props) => {
  // Prepare state constant with presenter
  const {
    state,
    onCloseDialog,
    onAcceptDialog,
  } = usePresenter(props);

  // Access state constants
  const {
    isLoading,
    microAppFeatures,
    modelData,
  } = state;

  // Fetch navigation configs from API
  const { navigationConfigs, isLoading: isNavLoading } = useNavigationConfigs();

  // Style object
  const classes = useStyles();
  const isMobileLogin = window.sessionStorage.getItem(IS_MOBILE_LOGIN) == 'true';
  const logicLayerApp = microAppFeatures?.find((app) => app.microapp === MICRO_APP_LOGIC_LAYER);

  // Replacement configs (have a microapp in path)
  const replacementConfigs = navigationConfigs.filter((config) => config.path && config.path !== "");
  const replacedMicroApps = new Set(replacementConfigs.map((config) => config.path));

  // Standalone configs (no microapp in path)
  const standaloneConfigs = navigationConfigs.filter((config) => !config.path || config.path === "");

  return (
    <>
      <Grid container className={classes.container} spacing={3}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <PageTitles title={useTranslate("administration:title")} subtitle={useTranslate("administration:subTitle")} />
          <Typography variant="body2" component="span">
            {useTranslate("administration:description")}
          </Typography>
          <br />
        </Grid>
        <Grid item xs={12} sm={6} md={9} lg={9}>
          <Grid container className={classes.container} spacing={3}>
            {microAppFeatures
              ?.sort((oldApp: IMicroAppFeature, newApp: IMicroAppFeature) => Number(oldApp.order) - Number(newApp.order))
              .map((microAppFeature: IMicroAppFeature, ind: number) => {
                // Filter and ignore the Micro apps with empty `domain`
                if (!microAppFeature.domain || microAppFeature.domain === "") {
                  return null;
                }
                if (microAppFeature.hideInMobile && isMobileLogin) {
                  return null;
                }

                // Check if this micro-app is replaced by a navigation config
                if (replacedMicroApps.has(microAppFeature.microapp) && logicLayerApp) {
                  const navConfig = replacementConfigs.find((config) => config.path === microAppFeature.microapp);
                  if (navConfig) {
                    return (
                      <NavigationMenuSection
                        key={`nav-${navConfig.id}`}
                        navigationConfig={navConfig}
                        microAppFeature={logicLayerApp}
                      />
                    );
                  }
                }

                return (
                  <MicroAppListItem
                    key={`key-${microAppFeature.microapp}-${ind}`}
                    microAppFeature={microAppFeature}
                    isMobileLogin={isMobileLogin}
                  />
                );
              })}
            {logicLayerApp && standaloneConfigs.map((config) => (
              <NavigationMenuSection
                key={`nav-standalone-${config.id}`}
                navigationConfig={config}
                microAppFeature={logicLayerApp}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
      <div>{isLoading || isNavLoading ? <LoadingIcon /> : null}</div>
      <ConfirmDialog
        title={useTranslate("administration:dialogTitle")}
        subTitle={useTranslate("administration:dialogSubTitle")}
        open={modelData.show}
        onClose={onCloseDialog}
        onOk={onAcceptDialog}
      >
        <Typography style={{ marginTop: -12 }}>
          <div dangerouslySetInnerHTML={{ __html: modelData.message || "" }} />
        </Typography>
      </ConfirmDialog>
    </>
  );
};
